import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TablePagination, Button, TextField, MenuItem,
  Select, FormControl, InputLabel, IconButton, Chip, Dialog, DialogTitle,
  DialogContent, DialogActions, Grid, Card, CardContent, Tabs, Tab,
  List, ListItem, ListItemText, Divider, Avatar, CircularProgress,
  Alert
} from '@mui/material';
import { Visibility, LocalShipping, Check, Search, Receipt, Refresh } from '@mui/icons-material';
import axios from 'axios';

const API_BASE = 'https://api.bharatdarshan.app/api';
const IMG_URL = 'https://api.bharatdarshan.app/';

const OrderManagement = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalOrders, setTotalOrders] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [detailDialog, setDetailDialog] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [stats, setStats] = useState({ total: 0, initiated: 0, accepted: 0, packed: 0, outForDelivery: 0, delivered: 0, cancelled: 0 });

  const orderStatuses = ['INITIATED', 'ACCEPTED', 'REJECTED', 'PACKED', 'OUT_FOR_DELIVERY', 'DELIVERED', 'CANCELLED'];

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      params.append('page', page + 1);
      params.append('limit', rowsPerPage);
      if (statusFilter !== 'all') params.append('status', statusFilter);

      const res = await axios.get(`${API_BASE}/marketplace/admin/orders?${params.toString()}`);
      if (res.data?.success) {
        setOrders(res.data.orders || []);
        setTotalOrders(res.data.pagination?.total || 0);
      } else {
        setError(res.data?.message || 'Failed to fetch orders');
      }
    } catch (err) {
      console.error('Fetch orders error:', err);
      setError(err.response?.data?.message || 'Failed to fetch orders');
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, statusFilter]);

  // Fetch stats
  const fetchStats = useCallback(async () => {
    try {
      const promises = ['INITIATED', 'ACCEPTED', 'PACKED', 'OUT_FOR_DELIVERY', 'DELIVERED', 'CANCELLED'].map(status =>
        axios.get(`${API_BASE}/marketplace/admin/orders?status=${status}&limit=1`).then(r => ({ status, count: r.data?.pagination?.total || 0 }))
      );
      const results = await Promise.all(promises);
      const totalRes = await axios.get(`${API_BASE}/marketplace/admin/orders?limit=1`);
      const newStats = { total: totalRes.data?.pagination?.total || 0 };
      results.forEach(r => {
        const key = r.status.toLowerCase().replace(/_([a-z])/g, (m, c) => c.toUpperCase());
        newStats[key] = r.count;
      });
      setStats(newStats);
    } catch (err) {
      console.log('Stats fetch error:', err.message);
    }
  }, []);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  useEffect(() => {
    fetchStats();
  }, []);

  const getStatusColor = (status) => {
    const colors = {
      'INITIATED': 'warning', 'ACCEPTED': 'info', 'REJECTED': 'error', 'PACKED': 'primary',
      'OUT_FOR_DELIVERY': 'secondary', 'DELIVERED': 'success', 'CANCELLED': 'error'
    };
    return colors[status] || 'default';
  };

  const getPaymentMethodColor = (method) => {
    if (method === 'WALLET') return 'success';
    if (method === 'RAZORPAY') return 'primary';
    return 'default';
  };

  // Extract customer info from order
  const getCustomerName = (order) => {
    if (order.customerName) return order.customerName;
    if (typeof order.customerId === 'object' && order.customerId?.name) return order.customerId.name;
    return 'N/A';
  };

  const getCustomerPhone = (order) => {
    if (typeof order.customerId === 'object' && order.customerId?.mobile) return order.customerId.mobile;
    return order.customerId || 'N/A';
  };

  // Extract vendor info from product
  const getVendorName = (order) => {
    const product = order.products?.[0]?.productId;
    if (typeof product?.vendorId === 'object') return product.vendorId.businessName || product.vendorId.ownerName || 'Vendor';
    return 'Vendor';
  };

  // Format address
  const getShippingAddress = (order) => {
    const addr = order.addressId;
    if (!addr) return 'No address';
    return `${addr.name || ''}, ${addr.address || ''}, ${addr.city || ''}, ${addr.state || ''} - ${addr.pincode || ''}`;
  };

  const formatCurrency = (amt) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(amt || 0);
  const formatDate = (date) => date ? new Date(date).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }) : 'N/A';

  // Filter by search (client-side for loaded orders)
  const filteredOrders = searchQuery
    ? orders.filter(o => {
        const name = getCustomerName(o).toLowerCase();
        const phone = getCustomerPhone(o);
        const id = (o._id || '').toLowerCase();
        const q = searchQuery.toLowerCase();
        return name.includes(q) || phone.includes(q) || id.includes(q);
      })
    : orders;

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#4CAF50' }}>Order Management</Typography>
        <Button variant="outlined" color="primary" startIcon={<Refresh />} onClick={() => { fetchOrders(); fetchStats(); }}>
          Refresh
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Stats Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          { label: 'Total Orders', value: stats.total, color: '#2196F3' },
          { label: 'Initiated', value: stats.initiated || 0, color: '#FF9800' },
          { label: 'Accepted', value: stats.accepted || 0, color: '#9C27B0' },
          { label: 'Packed', value: stats.packed || 0, color: '#00BCD4' },
          { label: 'Delivered', value: stats.delivered || 0, color: '#4CAF50' },
          { label: 'Cancelled', value: stats.cancelled || 0, color: '#F44336' }
        ].map((stat, i) => (
          <Grid item xs={6} sm={4} md={2} key={i}>
            <Card sx={{ bgcolor: stat.color, color: 'white', textAlign: 'center' }}>
              <CardContent sx={{ py: 1 }}>
                <Typography variant="h5" fontWeight="bold">{stat.value}</Typography>
                <Typography variant="body2">{stat.label}</Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <TextField fullWidth size="small" placeholder="Search by Order ID, Customer Name, Phone..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} InputProps={{ startAdornment: <Search sx={{ mr: 1, color: 'gray' }} /> }} />
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Order Status</InputLabel>
              <Select value={statusFilter} label="Order Status" onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}>
                <MenuItem value="all">All Status</MenuItem>
                {orderStatuses.map(s => <MenuItem key={s} value={s}>{s.replace(/_/g, ' ')}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <Typography variant="body2" color="textSecondary" textAlign="center">
              Showing {filteredOrders.length} of {totalOrders} orders
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      {/* Table */}
      <TableContainer component={Paper}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Table>
            <TableHead sx={{ bgcolor: '#4CAF50' }}>
              <TableRow>
                {['Order ID', 'Customer', 'Vendor', 'Products', 'Amount', 'Payment', 'Status', 'Date', 'Actions'].map(h => (
                  <TableCell key={h} sx={{ color: 'white', fontWeight: 'bold' }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredOrders.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center">
                    <Typography color="textSecondary" sx={{ py: 4 }}>No orders found</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredOrders.map((order) => (
                  <TableRow key={order._id} hover>
                    <TableCell>
                      <Typography fontWeight="bold" color="primary" sx={{ fontSize: 12 }}>
                        {order.invoiceId || order._id?.slice(-8).toUpperCase()}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography>{getCustomerName(order)}</Typography>
                      <Typography variant="caption" color="textSecondary">{getCustomerPhone(order)}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{getVendorName(order)}</Typography>
                    </TableCell>
                    <TableCell>
                      {order.products?.length || 0} item(s)
                    </TableCell>
                    <TableCell>
                      <Typography fontWeight="bold">{formatCurrency(order.grandTotal || order.totalPaid)}</Typography>
                      {(order.productTotal || order.deliveryCharge != null) && (
                        <Typography variant="caption" color="textSecondary">
                          Prod: {formatCurrency(order.productTotal)} + Del: {formatCurrency(order.deliveryCharge)}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={order.paymentMethod || 'RAZORPAY'}
                        color={getPaymentMethodColor(order.paymentMethod)}
                        size="small"
                        sx={{ mb: 0.5 }}
                      />
                      {order.paymentId && (
                        <Typography variant="caption" display="block" color="textSecondary" sx={{ fontSize: 10 }}>
                          {order.paymentId.length > 12 ? order.paymentId.slice(0, 12) + '...' : order.paymentId}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip label={order.status?.replace(/_/g, ' ')} color={getStatusColor(order.status)} size="small" />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{formatDate(order.createdAt)}</Typography>
                    </TableCell>
                    <TableCell>
                      <IconButton color="primary" onClick={() => { setSelectedOrder(order); setDetailDialog(true); setTabValue(0); }}>
                        <Visibility />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        )}
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={totalOrders}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(e, p) => setPage(p)}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
        />
      </TableContainer>

      {/* Order Detail Dialog */}
      <Dialog open={detailDialog} onClose={() => setDetailDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ bgcolor: '#4CAF50', color: 'white' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">
              Order Details - {selectedOrder?.invoiceId || selectedOrder?._id?.slice(-8).toUpperCase()}
            </Typography>
            {selectedOrder && <Chip label={selectedOrder.status?.replace(/_/g, ' ')} sx={{ bgcolor: 'white', color: '#4CAF50' }} />}
          </Box>
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          {selectedOrder && (
            <>
              <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)} sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}>
                <Tab label="Order Info" icon={<Receipt />} iconPosition="start" />
                <Tab label="Products" icon={<LocalShipping />} iconPosition="start" />
              </Tabs>

              {tabValue === 0 && (
                <Grid container spacing={2}>
                  {/* Customer Details */}
                  <Grid item xs={6}>
                    <Paper sx={{ p: 2 }}>
                      <Typography variant="subtitle2" color="textSecondary">Customer Details</Typography>
                      <Typography fontWeight="bold">{getCustomerName(selectedOrder)}</Typography>
                      <Typography>{getCustomerPhone(selectedOrder)}</Typography>
                      <Typography variant="body2" sx={{ mt: 1 }}>{getShippingAddress(selectedOrder)}</Typography>
                      {selectedOrder.addressId?.phone && (
                        <Typography variant="body2">📞 {selectedOrder.addressId.phone}</Typography>
                      )}
                    </Paper>
                  </Grid>

                  {/* Vendor Details */}
                  <Grid item xs={6}>
                    <Paper sx={{ p: 2 }}>
                      <Typography variant="subtitle2" color="textSecondary">Vendor Details</Typography>
                      <Typography fontWeight="bold">{getVendorName(selectedOrder)}</Typography>
                      {(() => {
                        const vendor = selectedOrder.products?.[0]?.productId?.vendorId;
                        if (typeof vendor === 'object') {
                          return (
                            <>
                              {vendor.ownerName && <Typography variant="body2">Owner: {vendor.ownerName}</Typography>}
                              {vendor.city && <Typography variant="body2">City: {vendor.city}</Typography>}
                              {vendor.phone && <Typography variant="body2">📞 {vendor.phone}</Typography>}
                            </>
                          );
                        }
                        return null;
                      })()}
                    </Paper>
                  </Grid>

                  {/* Financial Summary */}
                  <Grid item xs={4}>
                    <Paper sx={{ p: 2, textAlign: 'center' }}>
                      <Typography variant="subtitle2" color="textSecondary">Grand Total</Typography>
                      <Typography variant="h5" fontWeight="bold" color="primary">{formatCurrency(selectedOrder.grandTotal || selectedOrder.totalPaid)}</Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={4}>
                    <Paper sx={{ p: 2, textAlign: 'center' }}>
                      <Typography variant="subtitle2" color="textSecondary">Product Total</Typography>
                      <Typography variant="h5" fontWeight="bold" color="secondary">{formatCurrency(selectedOrder.productTotal)}</Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={4}>
                    <Paper sx={{ p: 2, textAlign: 'center' }}>
                      <Typography variant="subtitle2" color="textSecondary">Delivery Charge</Typography>
                      <Typography variant="h5" fontWeight="bold" color={selectedOrder.deliveryCharge === 0 ? 'success.main' : 'text.primary'}>
                        {selectedOrder.deliveryCharge === 0 ? 'FREE' : formatCurrency(selectedOrder.deliveryCharge)}
                      </Typography>
                    </Paper>
                  </Grid>

                  {/* Payment Details */}
                  <Grid item xs={6}>
                    <Typography>
                      <b>Payment Method:</b>{' '}
                      <Chip
                        label={selectedOrder.paymentMethod || 'RAZORPAY'}
                        color={getPaymentMethodColor(selectedOrder.paymentMethod)}
                        size="small"
                      />
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography>
                      <b>Payment ID:</b> {selectedOrder.paymentId || 'N/A'}
                    </Typography>
                  </Grid>

                  {/* Order ID */}
                  <Grid item xs={6}>
                    <Typography><b>Order ID:</b> {selectedOrder._id}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography><b>Invoice:</b> {selectedOrder.invoiceId || 'N/A'}</Typography>
                  </Grid>

                  {/* Dates */}
                  <Grid item xs={6}>
                    <Typography><b>Created:</b> {formatDate(selectedOrder.createdAt)}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography><b>Last Updated:</b> {formatDate(selectedOrder.statusUpdatedAt || selectedOrder.updatedAt)}</Typography>
                  </Grid>
                </Grid>
              )}

              {tabValue === 1 && (
                <List>
                  {selectedOrder.products?.map((item, idx) => {
                    const product = item.productId || {};
                    const vendor = typeof product.vendorId === 'object' ? product.vendorId : {};
                    const category = typeof product.categoryId === 'object' ? product.categoryId : {};
                    const subCategory = typeof product.subCategoryId === 'object' && product.subCategoryId ? product.subCategoryId : null;
                    const childCategory = typeof product.childCategoryId === 'object' && product.childCategoryId ? product.childCategoryId : null;
                    return (
                      <React.Fragment key={idx}>
                        <ListItem sx={{ alignItems: 'flex-start' }}>
                          {(product.bannerImages?.[0] || product.image) && (
                            <Avatar
                              src={IMG_URL + (product.bannerImages?.[0] || product.image)}
                              variant="rounded"
                              sx={{ width: 60, height: 60, mr: 2 }}
                            />
                          )}
                          <ListItemText
                            primary={<Typography fontWeight="bold">{product.productName || 'Product'}</Typography>}
                            secondary={
                              <>
                                {category.categoryName && <span>Category: {category.categoryName}{subCategory && subCategory.subCategoryName ? ' > ' + subCategory.subCategoryName : ''}{childCategory && childCategory.childCategoryName ? ' > ' + childCategory.childCategoryName : ''} | </span>}
                                Qty: {item.quantity || 1} | Price: {formatCurrency(product.price)}
                                {vendor.businessName && <span> | Vendor: {vendor.businessName}</span>}
                              </>
                            }
                          />
                          <Box sx={{ textAlign: 'right' }}>
                            <Typography fontWeight="bold">{formatCurrency((product.price || 0) * (item.quantity || 1))}</Typography>
                            {product.mrp > product.price && (
                              <Typography variant="caption" sx={{ textDecoration: 'line-through', color: 'text.secondary' }}>
                                {formatCurrency(product.mrp)}
                              </Typography>
                            )}
                          </Box>
                        </ListItem>
                        {idx < selectedOrder.products.length - 1 && <Divider />}
                      </React.Fragment>
                    );
                  })}
                </List>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default OrderManagement;
